"use client";

import type { RefObject } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
  Clock,
  Download,
  ExternalLink,
  FlaskConical,
  Loader2,
  WalletCards,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  DEFAULT_STILL_CHECKING_LABEL,
  type QrisLabels,
} from "./qris-payment-step.helpers";
import type { QrisExpiryPhase } from "./use-qris-countdown";

interface QrisPendingPanelProps {
  labels: QrisLabels;
  effectiveQr: string | null;
  invoiceUrl?: string | null;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  canSimulate: boolean;
  isSimulating: boolean;
  onSimulate: () => void;
  onSaveImage: () => void;
  phase: QrisExpiryPhase;
  secondsLeft: number;
  countdownLabel: string;
  layout: "stacked" | "split";
}

export function QrisPendingPanel({
  labels,
  effectiveQr,
  invoiceUrl,
  canvasRef,
  canSimulate,
  isSimulating,
  onSimulate,
  onSaveImage,
  phase,
  secondsLeft,
  countdownLabel,
  layout,
}: QrisPendingPanelProps) {
  const isSplit = layout === "split";
  const isWalletOnly = !effectiveQr && Boolean(invoiceUrl);
  const qrSize = isSplit ? 200 : 232;
  const walletLabel =
    labels.payInWallet ?? "Bayar lewat aplikasi dompet atau bank";

  const walletCta = invoiceUrl ? (
    <Button
      asChild
      type="button"
      className={cn(
        "min-h-11 w-full min-w-0 gap-2 bg-brand-primary px-3 text-white hover:bg-brand-primary-hover",
        effectiveQr && "lg:hidden",
      )}
    >
      <a href={invoiceUrl} rel="noreferrer" className="min-w-0">
        <WalletCards className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 text-balance">{walletLabel}</span>
        <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
      </a>
    </Button>
  ) : null;

  // Normally no heading: the selected tab already names the method, so an
  // h3 repeating it was the same word twice in a row. Wallet-only is the
  // exception, because there the tab still says QRIS while the method has
  // become something else, and the buyer needs that contradiction named.
  const headerBlock = (
    <div className="min-w-0 space-y-1">
      {isWalletOnly ? (
        <h3 className="break-words text-lg font-semibold text-text-heading">
          {labels.walletOnlyTitle ?? walletLabel}
        </h3>
      ) : null}
      <p className="break-words text-sm text-text-muted">
        {isWalletOnly
          ? (labels.walletOnlyInstruction ?? labels.instruction)
          : labels.instruction}
      </p>
    </div>
  );

  const qrBlock = (
    <div
      className={cn(
        "shrink-0 rounded-2xl border border-border-primary bg-white shadow-card",
        isSplit ? "p-3" : "p-4",
      )}
    >
      {effectiveQr ? (
        <QRCodeCanvas
          ref={canvasRef}
          value={effectiveQr}
          size={qrSize}
          level="M"
          marginSize={2}
          aria-label={labels.qrCodeLabel ?? "QRIS payment code"}
        />
      ) : (
        <div
          className="flex items-center justify-center"
          style={{ width: qrSize, height: qrSize }}
        >
          <Loader2
            className="h-8 w-8 animate-spin text-brand-primary motion-reduce:animate-none"
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );

  const detailsBlock = (
    <>
      {!isWalletOnly ? (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onSaveImage}
            disabled={!effectiveQr}
            className="min-h-11 w-full gap-2"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            {labels.saveImage}
          </Button>
          <p className="w-full min-w-0 break-words text-xs text-text-muted">
            {labels.saveHint}
          </p>
        </>
      ) : null}

      {canSimulate ? (
        <Button
          type="button"
          variant="outline"
          onClick={onSimulate}
          disabled={isSimulating || !effectiveQr}
          className="min-h-11 w-full gap-2 border-dashed border-status-warning-border text-status-warning-text hover:bg-status-warning-bg hover:text-status-warning-text"
        >
          {isSimulating ? (
            <Loader2
              className="h-4 w-4 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          ) : (
            <FlaskConical className="h-4 w-4" aria-hidden="true" />
          )}
          Simulate payment (dev)
        </Button>
      ) : null}

      {phase === "grace" ? (
        <div
          className="flex w-full min-w-0 items-center gap-2 text-sm text-status-warning-text"
          role="status"
          aria-live="polite"
        >
          <Clock className="h-4 w-4" aria-hidden="true" />
          <span className="min-w-0 break-words">
            {labels.stillChecking ?? DEFAULT_STILL_CHECKING_LABEL}
          </span>
        </div>
      ) : (
        <div
          className={cn(
            "flex w-full min-w-0 items-center gap-2 text-sm",
            secondsLeft <= 30 ? "text-status-error-text" : "text-text-muted",
          )}
        >
          <Clock className="h-4 w-4" aria-hidden="true" />
          <span className="min-w-0 break-words">{countdownLabel}</span>
        </div>
      )}
    </>
  );

  if (isSplit) {
    return (
      <div className="flex w-full min-w-0 flex-col items-center gap-4 text-center sm:items-start sm:text-left">
        {headerBlock}
        {walletCta}
        <div
          className={cn(
            "flex w-full flex-col items-center gap-4",
            !isWalletOnly &&
              "sm:grid sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start sm:gap-6",
          )}
        >
          {isWalletOnly ? null : qrBlock}
          <div className="flex w-full min-w-0 flex-col items-center gap-3 text-center sm:items-start sm:text-left">
            {detailsBlock}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col items-center gap-4 text-center">
      {headerBlock}
      {walletCta}
      {isWalletOnly ? null : qrBlock}
      {detailsBlock}
    </div>
  );
}
